import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import emailMask from 'text-mask-addons/dist/emailMask';
import NotificationSystem from 'react-notification-system';
import get from 'lodash/get';
import Loading from 'react-loading-components';

import Header from 'components/Header';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import FormGroupLabel from 'components/FormGroupLabel';
import { Button } from 'components/Button';
import Person from 'components/Icons/Person';
import Envelope from 'components/Icons/Envelope';
import Dollar from 'components/Icons/Dollar';
import Validator from 'components/Validator/Validator';
import Modal from 'components/Modal/AnimatedModal';
import { Grid, Column } from 'components/Layout';

import { checkinAction } from 'actions/workflow';
import { getApplications, getTestValuesData } from 'actions/application';

import { toTitleCase } from 'utils/toTitleCase';
import { parseUrlParams } from 'utils/parseUrlParams';
import { getPrimaryPhoneNumber } from '../../../utils/helpers';
import { numberUnmask, currencyMask, tdaCurrencyMask, unmask } from 'utils/masks';
import schema from './schema';
import style from './style.scss';
import Social from 'components/Icons/Social';
import { appConfig } from 'config/appConfig';

const EmailApp = ({ history, validator }) => {
  const { values, errors, setValues, validate, onChangeHandler, resetErrors } = validator;
  const dispatch = useDispatch();
  const params = parseUrlParams(window.location.search);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingApplication, setIsLoadingApplication] = useState(false);
  const [isPreLoading, setIsPreLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [selectedTestSubject, setSelectedTestSubject] = useState(null);
  const [testSubjects, setTestSubjects] = useState([]);
  const [selectedLinkedMerchant, setSelectedLinkedMerchant] = useState('');
  const [isModalShown, setIsModalShown] = useState(false);
  const notification = useRef(null);
  const [linkedMerchants, setLinkedMerchants] = useState([]);

  const prepareSubMerchantData = () => {
    const merchantId = localStorage.getItem('merchantId');
    const businessName = localStorage.getItem('businessName');
    const subMerchants = localStorage.getItem('subMerchants');
    setSelectedLinkedMerchant(merchantId);
    if (subMerchants) {
      const subMerchantsData = JSON.parse(subMerchants) || [];
      setLinkedMerchants([{ merchantId, businessName }, ...subMerchantsData]);
    }
  };

  const preloadApplication = () => {
    setIsPreLoading(true);
    dispatch(getApplications({
      url: `/applications/${params.applicationId}`,
      success: (res) => {
        setValues({
          ClientId: get(res, 'channel.attributes.clientId'),
          firstName: get(res, 'applicant.firstName'),
          lastName: get(res, 'applicant.lastName'),
          phoneNumber: getPrimaryPhoneNumber(get(res, 'applicant.phoneNumbers')),
          email: get(res, 'applicant.email'),
          totalDebtAmount: (get(res, 'channel.attributes.totalDebtAmount', '') || '').toString(),
          monthlySavingsAmount: (get(res, 'channel.attributes.monthlySavingsAmount', '') || '').toString(),
          customerDedicatedAccountBalance: (get(res, 'channel.attributes.customerDedicatedAccountBalance', '') || '').toString(),
          monthsLeft: (get(res, 'channel.attributes.monthsLeft', '') || '').toString(),
          agent: (get(res, 'agent', '') || '').toString(),
          referenceId: (get(res, 'referenceId', '') || '').toString(),
        });
        setIsPreLoading(false);
      },
      fail: (err) => {
        if (err.response) {
          notification.current
          && notification.current.addNotification({
            message: 'Exists multiple applications with these client id and merchant.',
            level: 'error',
            position: 'tc',
          });
        } else {
          console.log('Error', err);
        }
        setIsPreLoading(false);
      },
    }));
  };

  const loadHandleSetValues = () => {
    dispatch(getTestValuesData({
      url: '/lookup/set-values-fsl-merchant-portal',
      success: (res) => {
        const testList = res.applications.attributes.values;
        setTestSubjects(testList);
        setSelectedTestSubject(JSON.stringify(testList[0].value));
      },
      fail: (err) => {
        console.log('Error', err);
      },
    }));
  };

  useEffect(() => {
    if (appConfig.isSetValueVisible) {
      loadHandleSetValues();
    }
    if (params.applicationId) {
      preloadApplication();
    }
    prepareSubMerchantData();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate(schema).isValid) {
      const formData = {
        ...values,
        ClientId: values && values.ClientId && values.ClientId.trim().replace(/\s\s+/g, ' '),
        monthlySavingsAmount: unmask(values.monthlySavingsAmount),
        totalDebtAmount: unmask(values.totalDebtAmount),
        customerDedicatedAccountBalance: unmask(values.customerDedicatedAccountBalance),
        merchantId: selectedLinkedMerchant || localStorage.getItem('merchantId'),
        phoneNumber: numberUnmask(values.phoneNumber),
      };

      applicationId && Object.assign(formData, { ApplicationId: applicationId });
      if (params.applicationId) {
        Object.assign(formData, { ApplicationId: params.applicationId });
      }

      setIsLoading(true);

      dispatch(checkinAction({
        data: formData,
        url: '/workflows/application/workflow/fsl1/start',
        success: (response) => {
          setIsLoading(false);
          if (response.message === 'Value of property `MerchantId` should not be empty') {
            notification.current
            && notification.current.addNotification({
              message: response.message,
              level: 'error',
              position: 'tc',
            });
          } else {
            history.push('/dashboard/congrats');
          }
        },
        fail: (error) => {
          error && error.data && error.data.errorMessages && notification.current && notification.current.addNotification({
            message: 'We have encountered an error, please try again.',
            level: 'error',
            position: 'tc',
          });
          setIsLoading(false);
        },
      }));
    }
  };

  const handleCheckMerchantSelected = (e) => {
    if (validate(schema).isValid && linkedMerchants && linkedMerchants.length > 1) {
      setIsModalShown(true);
    } else {
      handleSubmit(e);
    }
  };

  const handleInputChange = (event) => {
    switch (event.target.name) {
      case 'FirstName':
      case 'LastName':
        onChangeHandler(event.target.name, (event.target.value).replace(/[^a-zA-Z-' ]/g, ''));
        break;
      case 'MonthsLeft':
        onChangeHandler(event.target.name, (event.target.value).replace(/[^0-9' ]/g, ''));
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
        break;
    }
  };

  const handleBlur = (event) => {
    event.preventDefault();
    onChangeHandler(event.target.name, toTitleCase(event.target.value));
  };

  const handleRefreshClientId = (e) => {
    e.preventDefault();
    if (get(values, 'ClientId')) {
      setIsLoadingApplication(true);
      const clientId = values && values.ClientId ? values.ClientId : '';
      const merchantId = localStorage.getItem('merchantId') ? localStorage.getItem('merchantId') : '';
      setValues({});
      resetErrors();
      const url = `/applications-view/search?channelAttributesClientId=${clientId}&merchantId=${merchantId}&limit=${10}&channelCode=fsl`;
      dispatch(getApplications({
        url,
        success: (res) => {
          setIsLoadingApplication(false);
          if (res && res.data && res.data.length > 1) {
            notification.current
            && notification.current.addNotification({
              message: 'Exists multiple applications with these client id and merchant.',
              level: 'error',
              position: 'tc',
            });
            return;
          }
          if (!res || !res.data || !res.data.length) {
            notification.current
            && notification.current.addNotification({
              message: 'Client ID not found',
              level: 'error',
              position: 'tc',
            });
            return;
          }

          const application = res && res.data[0];
          setApplicationId(application.applicationId);

          let phone = application.phone ? numberUnmask(application.phone) : '';
          phone = phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
          setValues({
            firstName: get(application, 'applicantFirstName', ''),
            lastName: get(application, 'applicantLastName', ''),
            phoneNumber: phone || '',
            email: get(application, 'email', ''),
            totalDebtAmount: get(application, 'channelAttributes.totalDebtAmount', '').toString(),
            monthlySavingsAmount: get(application, 'channelAttributes.monthlySavingsAmount', '').toString(),
            customerDedicatedAccountBalance: get(application, 'channelAttributes.customerDedicatedAccountBalance', '').toString(),
            ClientId: get(application, 'channelAttributes.clientId', ''),
            monthsLeft: get(application, 'channelAttributes.monthsLeft', ''),
          });
          notification.current
          && notification.current.addNotification({
            message: 'Successfully loaded.',
            level: 'success',
            position: 'tc',
          });
        },
        fail: (err) => {
          setIsLoading(false);

          notification.current
          && notification.current.addNotification({
            message: err && err.data && err.data.message ? err.data.message : 'An unexpected error has occurred, please try again',
            level: 'error',
            position: 'tc',
          });
        },
      }));
    }
  };

  const handleSetValues = (e) => {
    e.preventDefault();
    const selectedOption = JSON.parse(selectedTestSubject);
    setValues(selectedOption);
  };

  return (
    <Fragment>
      <Header />
      <NotificationSystem ref={notification} />
      <section className="container section" style={{ position: 'relative' }}>
        <div className="d-flex align-items-center justify-content-space-between" style={{ margin: '20px 50px 0' }}>
          {appConfig.isSetValueVisible && testSubjects && testSubjects.length > 0 && (
          <div className="d-flex align-items-center " style={{ gap: 10, marginLeft: 50 }}>
            <select className="form-control m-0" style={{ width: 'auto', height: '40px' }} value={selectedTestSubject} onChange={(e) => { setSelectedTestSubject(e.target.value); }}>
              {testSubjects.map((item, i) => <option key={i} value={JSON.stringify(item.value)}>{item.title}</option>)}
            </select>
            <Button
              className={cn('button green m-0', style.button)}
              onClick={handleSetValues}
              color="primary"
              style={{ padding: 11 }}
            >
              SET VALUES
            </Button>
          </div>)}
        </div>
        <Modal
          className={style.modal}
          isShown={isModalShown}
          onClose={() => setIsModalShown(!isModalShown)}
          canBeClosedByEsc
          isLogoShown={false}
        >
          <div className={style['card-header']}>
            <h5>Merchant Selection</h5>
          </div>
          <p>
            Please confirm the selected merchant is correct:
          </p>
          <h4>{selectedLinkedMerchant && linkedMerchants.find(m => m.merchantId === selectedLinkedMerchant) && linkedMerchants.find(m => m.merchantId === selectedLinkedMerchant).businessName}</h4>
          <Button
            className={cn('button arrow green w-100', style.button)}
            onClick={handleSubmit}
          >
            ACCEPT
          </Button>
          <Button
            className={cn('button arrow w-100', style.button)}
            onClick={() => setIsModalShown(false)}
          >
            CANCEL
          </Button>
        </Modal>
        <div className="grid-container fluid borrowers-apply">
          <div className="grid-container">
            <Grid className="max-limited">
              <Column className="small-12 large-10 main-column large-offset-1">
                <div className="form-group">
                  <Grid className="grid-margin-x">
                    <Column className="small-12 medium-11 medium-offset-1">
                      <div className={style.headerWrapper}>
                        <FormGroupLabel value="Email App to Borrower Form" />
                        {linkedMerchants.length > 1 && (
                          <Select
                            name="form-field-name"
                            value={selectedLinkedMerchant}
                            onChange={e => setSelectedLinkedMerchant(e.target.value)}
                            label="Merchant"
                            data={linkedMerchants.map(item => ({ value: item.merchantId, title: `${item.businessName} - ${item.merchantId}` }))}
                            isErrorVisible={false}
                            isBadgeVisible={false}
                            style={{ width: 180, height: 40 }}
                            hasDefault={false}
                          />
                        )}
                      </div>
                    </Column>
                    <Column className="small-12 medium-1 medium-offset-1">
                      <div className={style.icon}><Person width={25} /></div>
                    </Column>
                    <Column className="small-12 medium-5">
                      <div className="d-flex">
                        <Input
                          label="Client ID"
                          name="ClientId"
                          className="rounded-right-0"
                          labelClassName="w-100"
                          value={values.ClientId}
                          onChange={handleInputChange}
                          hasError={!!errors.ClientId}
                          isRequired
                          errorMessage={errors.ClientId}
                          isDisabled={params.applicationId}
                        />
                        <Button
                          className={cn(style['refresh-btn'], 'button green', style.button)}
                          onClick={handleRefreshClientId}
                          isLoading={isLoadingApplication}
                          isDisabled={isLoadingApplication || params.applicationId}
                        >
                          Search
                        </Button>
                      </div>
                    </Column>
                    <Column className="small-12 medium-5">
                      <div />
                    </Column>
                    <Column className="small-12 medium-1 medium-offset-1">
                      <div className={style.icon}><Person width={25} /></div>
                    </Column>
                    <Column className="small-12 medium-5">
                      <Input
                        label="First Name"
                        name="firstName"
                        value={values.firstName}
                        onChange={handleInputChange}
                        hasError={!!errors.firstName}
                        isRequired
                        onBlur={handleBlur}
                        errorMessage={errors.firstName}
                      />
                    </Column>
                    <Column className="small-12 medium-5">
                      <Input
                        label="Last Name"
                        name="lastName"
                        value={values.lastName}
                        onChange={handleInputChange}
                        hasError={!!errors.lastName}
                        isRequired
                        onBlur={handleBlur}
                        errorMessage={errors.lastName}
                      />
                    </Column>
                    <Column className="small-12 medium-1 medium-offset-1">
                      <div className={style.icon}>
                        <Envelope width={25} />
                      </div>
                    </Column>
                    <Column className="small-12 medium-10">
                      <Input
                        label="Phone Number"
                        name="phoneNumber"
                        placeHolder="(___) ___-____"
                        isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                        value={values.phoneNumber}
                        onChange={handleInputChange}
                        hasError={!!errors.phoneNumber}
                        isRequired
                        errorMessage={errors.phoneNumber}
                      />
                    </Column>
                    <Column className="small-12 medium-1 medium-offset-1">
                      <div className={style.icon}>
                        <Envelope width={25} />
                      </div>
                    </Column>
                    <Column className="small-12 medium-10">
                      <Input
                        label="Email Address"
                        name="email"
                        isMasked={emailMask}
                        value={values.email}
                        onChange={handleInputChange}
                        hasError={!!errors.email}
                        isRequired
                        errorMessage={errors.email}
                      />
                    </Column>
                    <Column className="small-12 medium-1 medium-offset-1">
                      <div className={style.icon}>
                        <Dollar width={25} />
                      </div>
                    </Column>
                    <Column className="small-12 medium-5">
                      <Input
                        label="Requested Loan Amount"
                        name="totalDebtAmount"
                        value={values.totalDebtAmount}
                        onChange={handleInputChange}
                        isMasked={tdaCurrencyMask}
                        hasError={!!errors.totalDebtAmount}
                        placeHolder="($1,000.00 - $50,000.00)"
                        isRequired
                        errorMessage={errors.totalDebtAmount}
                      />
                    </Column>
                    <Column className="small-12 medium-5">
                      <div />
                    </Column>
                    <Column className="small-12 medium-1 medium-offset-1">
                      <div className={style.icon}>
                        <Dollar width={25} />
                      </div>
                    </Column>
                    <Column className="small-12 medium-5">
                      <Input
                        label="Monthly Savings Amount"
                        name="monthlySavingsAmount"
                        value={values.monthlySavingsAmount}
                        onChange={handleInputChange}
                        isMasked={currencyMask}
                        hasError={!!errors.monthlySavingsAmount}
                        isRequired
                        errorMessage={errors.monthlySavingsAmount}
                      />
                    </Column>
                    <Column className="small-12 medium-5">
                      <div />
                    </Column>
                    <Column className="small-12 medium-1 medium-offset-1">
                      <div className={style.icon}>
                        <Dollar width={25} />
                      </div>
                    </Column>
                    <Column className="small-12 medium-5">
                      <Input
                        label="Customer Dedicated Account Balance"
                        name="customerDedicatedAccountBalance"
                        value={values.customerDedicatedAccountBalance}
                        onChange={handleInputChange}
                        isMasked={currencyMask}
                        hasError={!!errors.customerDedicatedAccountBalance}
                        isRequired
                        errorMessage={errors.customerDedicatedAccountBalance}
                      />
                    </Column>
                    <Column className="small-12 medium-5">
                      <div />
                    </Column>
                    <Column className="small-12 medium-1 medium-offset-1">
                      <div className={style.icon}><Person width={25} /></div>
                    </Column>
                    <Column className="small-12 medium-5">
                      <Input
                        label="Months Left"
                        name="monthsLeft"
                        value={values.monthsLeft}
                        onChange={handleInputChange}
                        hasError={!!errors.monthsLeft}
                        onBlur={handleBlur}
                        errorMessage={errors.monthsLeft}
                        isRequired
                      />
                    </Column>
                    <Column className="small-12 medium-5">
                      <div />
                    </Column>
                    <Column className="small-12 medium-1 medium-offset-1">
                      <div className={style.icon}><Person width={25} /></div>
                    </Column>
                    <Column className="small-12 medium-5">
                      <Input
                        label="Agent"
                        name="agent"
                        value={values.agent}
                        onChange={handleInputChange}
                        hasError={!!errors.agent}
                        onBlur={handleBlur}
                        errorMessage={errors.agent}
                      />
                    </Column>

                    <Column className="small-12 medium-5">
                      <div />
                    </Column>
                    <Column className="small-12 medium-1 medium-offset-1">
                      <div className={style.icon}><Social width={25} /></div>
                    </Column>
                    <Column className="small-12 medium-5">
                      <Input
                        label="Reference ID"
                        name="referenceId"
                        value={values.referenceId}
                        onChange={handleInputChange}
                        hasError={!!errors.referenceId}
                        onBlur={handleBlur}
                        errorMessage={errors.referenceId}
                      />
                    </Column>
                    <Column className="small-12 medium-5">
                      <div />
                    </Column>
                    <Column className="small-12 medium-4 medium-offset-2">
                      <Button
                        className={cn('button green w-100', style.button)}
                        onClick={handleCheckMerchantSelected}
                        isLoading={isLoading}
                        isDisabled={isLoadingApplication || isLoading || isPreLoading}
                      >
                        Send
                      </Button>
                    </Column>
                  </Grid>
                </div>
              </Column>
            </Grid>
          </div>
        </div>
        {isPreLoading && (
          <div style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          >
            <Loading type="oval" width={70} height={70} fill="#3adb76" />
          </div>
        )}
      </section>
    </Fragment>
  );
};

EmailApp.propTypes = {
  validator: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

EmailApp.defaultProps = {

};

export default Validator(schema)(EmailApp);



// WEBPACK FOOTER //
// ./src/containers/pages/EmailApp/index.js